import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import last from 'lodash/last';

import { ItemDragLayer } from 'Shared/components/DragAndDropComponents';
import { WorkspaceItem, SelectedSort, ItemPosition } from 'Shared/constants/types';
import { saveActionItemOrder } from 'Shared/actions/Goals.actions';
import ActionItemsWithNestingTable from 'Shared/components/ActionItems/ActionItemsWithNestingTable/ActionItemsWithNestingTable';
import ReadOnlyActionItemsWithNestingTable from 'Shared/components/ActionItems/ReadOnlyActionItemsWithNestingTable/ReadOnlyActionItemsWithNestingTable';
import { createItemFromSourceItemAction } from 'Shared/actions/meetingItems';

import './GoalDetailsPageActionItemsList.scss';

type Props = {
  goalSlug: string;
  selectedSort: SelectedSort;
  actionItems: Array<WorkspaceItem>;
  setSelectedSort: Function;
  goalIsReadOnly: boolean;
  forceShowMobileActions?: boolean;
  showTableHeaders?: boolean;
};

function GoalDetailsPageActionItemsList({
  goalSlug,
  actionItems,
  selectedSort,
  setSelectedSort,
  goalIsReadOnly,
  forceShowMobileActions = false,
  showTableHeaders = true,
}: Props) {
  const dispatch = useDispatch();
  const lastItemUuid = last(actionItems)?.uuid;

  const handleActionItemDrop = useCallback(
    ({ draggedItemUuids, hoveredItemUuid, hoverOffset }) => {
      dispatch(
        saveActionItemOrder({
          draggedItemUuids,
          hoveredItemUuid: hoveredItemUuid || lastItemUuid,
          hoverOffset,
          goalSlug,
        })
      );
    },
    [dispatch, goalSlug, lastItemUuid]
  );

  const handleCreateOnEnter = useCallback(
    ({ sourceItemUuid, itemUuid }) => {
      dispatch(
        createItemFromSourceItemAction({
          sourceItemUuid,
          itemUuid,
          positionField: ItemPosition.positionForGoalDetailPage,
        })
      );
      dispatch(
        saveActionItemOrder({
          draggedItemUuids: [itemUuid],
          hoveredItemUuid: sourceItemUuid,
          hoverOffset: 1,
          goalSlug,
        })
      );
    },
    [dispatch, goalSlug]
  );

  return (
    <div className="GoalDetailsPageActionItemsList">
      <ItemDragLayer />
      {!goalIsReadOnly && (
        <ActionItemsWithNestingTable
          withOwnerSection={forceShowMobileActions ? false : true}
          withDueDateSection={forceShowMobileActions ? false : true}
          withWorkspaceSection={false}
          positionField={ItemPosition.positionForGoalDetailPage}
          actionItems={actionItems}
          selectedSort={selectedSort}
          onActionItemDrop={handleActionItemDrop}
          onCreateItem={handleCreateOnEnter}
          setSelectedSort={setSelectedSort}
          forceShowMobileActions={forceShowMobileActions}
          showTableHeaders={showTableHeaders}
        />
      )}
      {goalIsReadOnly && (
        <ReadOnlyActionItemsWithNestingTable
          withOwnerSection
          withWorkspaceSection={false}
          positionField={ItemPosition.positionForGoalDetailPage}
          actionItems={actionItems}
          selectedSort={selectedSort}
          onActionItemDrop={handleActionItemDrop}
          onCreateItem={handleCreateOnEnter}
          setSelectedSort={setSelectedSort}
        />
      )}
    </div>
  );
}

export default GoalDetailsPageActionItemsList;
